import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertCircle, Heart, Activity, Droplet, Moon, Smile, Target } from 'lucide-react';
import { Progress } from './ui/progress';

const DTSBreakdownModal = ({ open, onClose, breakdown }) => {
  if (!breakdown) return null;

  const getMetricIcon = (key) => {
    const icons = {
      heart_rate: Heart,
      hrv: Activity,
      spo2: Droplet,
      sleep: Moon,
      mood: Smile,
      habits: Target,
    };
    return icons[key] || Activity;
  };

  const getMetricLabel = (key) => {
    const labels = {
      heart_rate: 'Heart Rate',
      hrv: 'Heart Rate Variability',
      spo2: 'Blood Oxygen (SpO₂)',
      sleep: 'Sleep Duration',
      mood: 'Mood Score',
      habits: 'Habit Adherence',
    };
    return labels[key] || key;
  };

  const getMetricUnit = (key) => {
    const units = {
      heart_rate: 'bpm',
      hrv: 'ms',
      spo2: '%',
      sleep: 'hours',
      mood: '/10',
      habits: 'days',
    };
    return units[key] || '';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dts-breakdown-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center space-x-2">
            <Activity className="w-6 h-6" style={{ color: '#8F2FDF' }} />
            <span>Digital Twin Score Breakdown</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall Score */}
          <div className="text-center p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, #F0E7FF 0%, #FFE8F5 100%)' }}>
            <p className="text-sm text-gray-600 mb-2">Overall Digital Twin Score</p>
            <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', color: breakdown.zone === 'Green' ? '#32D583' : breakdown.zone === 'Yellow' ? '#FBBF24' : '#EF4444' }}>
              {breakdown.total_score}
            </div>
            <div className="inline-flex items-center px-4 py-2 rounded-full font-semibold text-white" style={{ background: breakdown.zone === 'Green' ? '#32D583' : breakdown.zone === 'Yellow' ? '#FBBF24' : '#EF4444' }}>
              {breakdown.zone} Zone
            </div>
          </div>

          {/* Explanation */}
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-gray-700">{breakdown.explanation}</p>
          </div>

          {/* Red Flags */}
          {breakdown.red_flags && breakdown.red_flags.length > 0 && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-center space-x-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-900">Active Alerts ({breakdown.red_flags.length})</h3>
              </div>
              <div className="space-y-2">
                {breakdown.red_flags.map((flag, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-red-800">{flag.metric}:</span>
                    <span className="text-red-700">
                      {flag.value} {flag.threshold}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Component Breakdown */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Score Components</h3>
            {Object.entries(breakdown.components).map(([key, data]) => {
              const Icon = getMetricIcon(key);
              return (
                <div key={key} className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100">
                        <Icon className="w-5 h-5" style={{ color: '#8F2FDF' }} />
                      </div>
                      <div>
                        <p className="font-medium">{getMetricLabel(key)}</p>
                        <p className="text-sm text-gray-600">
                          Current: {data.value} {getMetricUnit(key)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${getScoreColor(data.score)}`}>
                        {data.score}
                      </p>
                      <p className="text-xs text-gray-500">Weight: {data.weight}%</p>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(data.score)} transition-all`}
                        style={{ width: `${data.score}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Calculation Method */}
          <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <h4 className="font-semibold mb-2">How is DTS Calculated?</h4>
            <p className="text-sm text-gray-700">
              The Digital Twin Score is a weighted composite of six key health metrics. Each metric is scored 0-100 based on how close it is to the optimal target range, then multiplied by its weight percentage. The sum gives your overall DTS score.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DTSBreakdownModal;
